import React, { createContext, useContext, useEffect, useState } from 'react';
import axios from 'axios';

// Public-site reception state. Loads the list of receptions once from the
// open `/reception/getreceptions` endpoint and keeps the customer's choice
// in localStorage so every registration form, contact form and payment
// flow can stamp the correct `center` on the request.

const ReceptionContext = createContext();
const STORAGE_KEY = 'public_reception';

export const useReception = () => useContext(ReceptionContext);

const loadFromStorage = () => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return parsed && parsed._id ? parsed : null;
    } catch (_) {
        return null;
    }
};

const writeToStorage = (data) => {
    try {
        if (data) localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
        else localStorage.removeItem(STORAGE_KEY);
    } catch (_) {}
};

export const ReceptionProvider = ({ children }) => {
    const [receptions, setReceptions] = useState([]);
    const [selected, setSelectedState] = useState(loadFromStorage);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        axios
            .get(`${process.env.REACT_APP_BACKEND_URL}/reception/getreceptions`)
            .then((res) => {
                if (cancelled) return;
                const list = (res.data || []).filter((r) => r.isActive !== false);
                setReceptions(list);
                // If the saved choice no longer exists (renamed/deleted),
                // or there's no saved choice yet, fall back to the first one.
                const stored = loadFromStorage();
                const found = stored && list.find((r) => r._id === stored._id);
                if (found) {
                    setSelectedState({ _id: found._id, name: found.name });
                } else if (list.length > 0) {
                    const first = { _id: list[0]._id, name: list[0].name };
                    setSelectedState(first);
                    writeToStorage(first);
                }
            })
            .catch((err) => {
                console.error('Error loading receptions for public picker:', err);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => { cancelled = true; };
    }, []);

    const setSelected = (reception) => {
        const next = reception && reception._id
            ? { _id: String(reception._id), name: reception.name || '' }
            : null;
        writeToStorage(next);
        setSelectedState(next);
    };

    const activeReception = selected
        ? receptions.find((r) => r._id === selected._id) || null
        : null;

    return (
        <ReceptionContext.Provider value={{
            receptions,
            loading,
            selected,
            activeReception,
            centerId: selected?._id || null,
            centerName: selected?.name || '',
            allowedRegistrationTypes: activeReception?.allowedRegistrationTypes || null,
            setSelected,
        }}>
            {children}
        </ReceptionContext.Provider>
    );
};

export default ReceptionContext;
